import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './GoogleTranslate.scss';

const GoogleTranslate = () => {
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const dropdownRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const widgetInitializedRef = useRef(false);
  const translateElementRef = useRef(null);
  const observerRef = useRef(null);
  const isInitialMountRef = useRef(true);

  // List of valid routes that should be translated
  const validRoutes = [
    '/',
    '/home-v1',
    '/home-v2',
    '/about',
    '/methodology',
    '/contact-us',
    '/privacy-policy'
  ];

  // Check if current route should be translated
  const shouldTranslate = validRoutes.includes(location.pathname) ||
    location.pathname.startsWith('/country-energy-profile') ||
    location.pathname.startsWith('/impact-insights') ||
    location.pathname.startsWith('/blog/');

  // Language options - using Google Translate language codes
  const languages = [
    { code: '', name: 'English', codeDisplay: 'EN' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', codeDisplay: 'AR' },
    { code: 'fr', name: 'French', nativeName: 'Français', codeDisplay: 'FR' },
    { code: 'ha', name: 'Hausa', codeDisplay: 'HA' },
    { code: 'ff', name: 'Fulfulde', codeDisplay: 'FF' },
    { code: 'wo', name: 'Wolof', codeDisplay: 'WO' },
    { code: 'so', name: 'Somali', nativeName: 'Soomaali', codeDisplay: 'SO' }
  ];

  // Hide Google Translate official UI elements
  useEffect(() => {
    const hideGoogleTranslateUI = () => {
      // Hide banner frame
      const bannerFrame = document.querySelector('.goog-te-banner-frame');
      if (bannerFrame) {
        bannerFrame.style.display = 'none';
        bannerFrame.style.visibility = 'hidden';
        bannerFrame.style.height = '0';
        bannerFrame.style.width = '0';
        bannerFrame.style.opacity = '0';
      }

      // Hide banner
      const banner = document.querySelector('.goog-te-banner');
      if (banner) {
        banner.style.display = 'none';
        banner.style.visibility = 'hidden';
        banner.style.height = '0';
      }

      // Hide skip translate link
      const skipTranslate = document.querySelector('.skiptranslate');
      if (skipTranslate) {
        skipTranslate.style.display = 'none';
        skipTranslate.style.visibility = 'hidden';
      }

      // Hide all Google Translate iframes
      const iframes = document.querySelectorAll('iframe[src*="translate"], iframe[title*="Translate"]');
      iframes.forEach(iframe => {
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        iframe.style.height = '0';
        iframe.style.width = '0';
      });
    };

    // Run immediately and set up observer
    hideGoogleTranslateUI();
    
    // Use MutationObserver to catch dynamically added elements
    const observer = new MutationObserver(() => {
      hideGoogleTranslateUI();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also run periodically to catch any missed elements
    const interval = setInterval(hideGoogleTranslateUI, 500);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Helper function to clear all possible cookie variations
  const clearGoogleTranslateCookie = () => {
    const pastDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
    const hostname = window.location.hostname;
    const domain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    
    // Clear cookie with all possible domain/path combinations
    const cookieVariations = [
      `googtrans=; expires=${pastDate}; path=/;`,
      `googtrans=; expires=${pastDate}; path=/; domain=${hostname};`,
      `googtrans=; expires=${pastDate}; path=/; domain=.${hostname};`,
      `googtrans=; expires=${pastDate}; path=/; domain=${domain};`,
      `googtrans=; expires=${pastDate}; path=/; domain=.${domain};`,
      `googtrans=; expires=${pastDate}; path=/; domain=.${hostname.split('.').slice(-2).join('.')};`
    ];
    
    cookieVariations.forEach(cookie => {
      document.cookie = cookie;
    });
  };

  // Detect current language from cookie or hash on mount
  useEffect(() => {
    if (!shouldTranslate) return;

    // Check for language in URL hash
    const hashMatch = window.location.hash.match(/googtrans\(([^)]+)\)/);
    if (hashMatch) {
      const langCode = hashMatch[1];
      if (langCode === 'en') {
        setSelectedLanguage('');
      } else {
        setSelectedLanguage(langCode);
      }
      return;
    }

    // Check for language in cookie
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'googtrans' && value) {
        const langMatch = value.match(/\/en\/([^/]+)/);
        if (langMatch) {
          setSelectedLanguage(langMatch[1]);
          return;
        }
      }
    }

    // Default to English if no language is set
    setSelectedLanguage('');
  }, [shouldTranslate, location.pathname]);

  // Sync cookie when selectedLanguage changes (safety check)
  // This ensures cookie is cleared and set correctly when language changes via widget
  // Note: This is a safety mechanism - handleLanguageSelect already handles cookie clearing
  useEffect(() => {
    if (!shouldTranslate) return;
    
    // Skip on initial mount - the cookie detection useEffect handles initial state
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    
    // Only sync if selectedLanguage changed (not from initial detection)
    const currentCookie = document.cookie
      .split(';')
      .find(c => c.trim().startsWith('googtrans='));
    
    if (currentCookie) {
      const cookieValue = currentCookie.split('=')[1];
      const langMatch = cookieValue ? cookieValue.match(/\/en\/([^/]+)/) : null;
      const cookieLang = langMatch ? langMatch[1] : '';
      
      // If cookie doesn't match selectedLanguage, sync them
      if (cookieLang !== selectedLanguage) {
        clearGoogleTranslateCookie();
        if (selectedLanguage !== '') {
          document.cookie = `googtrans=/en/${selectedLanguage}; path=/; max-age=31536000`;
        }
      }
    } else if (selectedLanguage !== '') {
      // Cookie doesn't exist but selectedLanguage is set, set the cookie
      document.cookie = `googtrans=/en/${selectedLanguage}; path=/; max-age=31536000`;
    } else {
      // selectedLanguage is empty, ensure cookie is cleared
      clearGoogleTranslateCookie();
    }
  }, [selectedLanguage, shouldTranslate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Initialize Google Translate
  useEffect(() => {
    if (!shouldTranslate) {
      const translateElement = document.getElementById('google_translate_element');
      if (translateElement) {
        translateElement.innerHTML = '';
      }
      widgetInitializedRef.current = false;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    // Function to find select element
    const findSelectElement = () => {
      // Try multiple selectors
      let select = document.querySelector('.goog-te-combo');
      if (!select) {
        select = document.querySelector('select.goog-te-combo');
      }
      if (!select) {
        select = document.querySelector('#google_translate_element select');
      }
      if (!select) {
        const translateElement = document.getElementById('google_translate_element');
        if (translateElement) {
          select = translateElement.querySelector('select');
          // Also check all descendants
          if (!select) {
            select = translateElement.querySelector('* select');
          }
        }
      }
      // Check in all iframes
      if (!select) {
        try {
          const frames = document.querySelectorAll('iframe');
          for (let frame of frames) {
            try {
              if (frame.contentDocument) {
                select = frame.contentDocument.querySelector('.goog-te-combo') ||
                        frame.contentDocument.querySelector('select');
                if (select) break;
              }
            } catch (e) {
              // Cross-origin, ignore
            }
          }
        } catch (e) {
          // Ignore
        }
      }
      return select;
    };

    // Setup MutationObserver to watch for select element creation
    const setupObserver = () => {
      const translateElement = document.getElementById('google_translate_element');
      if (!translateElement) return;

      // Disconnect previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create new observer
      observerRef.current = new MutationObserver((mutations) => {
        const select = findSelectElement();
        if (select) {
          widgetInitializedRef.current = true;
          if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
          }
        }
      });

      // Start observing
      observerRef.current.observe(translateElement, {
        childList: true,
        subtree: true,
        attributes: false
      });

      // Also observe document body for iframe creation
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true
      });
    };

    // Initialize Google Translate callback
    window.googleTranslateElementInit = () => {
      const translateElement = document.getElementById('google_translate_element');
      if (!translateElement) {
        return;
      }

      translateElementRef.current = translateElement;

      // Clear previous content
      translateElement.innerHTML = '';
      widgetInitializedRef.current = false;

      try {
        if (window.google && window.google.translate) {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: 'ar,fr,ha,ff,wo,so',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: false,
              multilanguagePage: true
            },
            'google_translate_element'
          );
          
          // Setup observer immediately
          setupObserver();
          
          // Also do periodic checks as backup
          let checkCount = 0;
          const checkForSelect = setInterval(() => {
            checkCount++;
            const select = findSelectElement();
            
            if (select) {
              widgetInitializedRef.current = true;
              clearInterval(checkForSelect);
              if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
              }
            } else {
              // Log progress every 10 checks for debugging
              if (checkCount % 10 === 0) {
                if (translateElement.innerHTML.length > 0) {
                }
              }
              
              if (checkCount > 50) {
                // Stop checking after 5 seconds
                clearInterval(checkForSelect);
              }
            }
          }, 100);
        } else {
        }
      } catch (error) {
      }
    };

    // Load Google Translate script if not already loaded
    if (!scriptLoadedRef.current) {
      const existingScript = document.querySelector('script[src*="translate.google.com"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        script.onerror = () => {
        };
        document.body.appendChild(script);
        scriptLoadedRef.current = true;
      } else {
        scriptLoadedRef.current = true;
        // If script already exists, wait a bit then initialize
        setTimeout(() => {
          if (window.google && window.google.translate) {
            window.googleTranslateElementInit();
          } else {
            // Script exists but API not ready, wait more
            setTimeout(() => {
              if (window.google && window.google.translate) {
                window.googleTranslateElementInit();
              }
            }, 500);
          }
        }, 200);
      }
    } else {
      // Script already loaded, try to initialize
      if (window.google && window.google.translate) {
        widgetInitializedRef.current = false;
        window.googleTranslateElementInit();
      } else {
        // Wait for API to be ready
        setTimeout(() => {
          if (window.google && window.google.translate) {
            widgetInitializedRef.current = false;
            window.googleTranslateElementInit();
          }
        }, 300);
      }
    }

    return () => {
      widgetInitializedRef.current = false;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [shouldTranslate, location.pathname]);

  // Handle language selection - always reload page
  const handleLanguageSelect = (languageCode, languageName) => {
    setSelectedLanguage(languageCode);
    setIsDropdownOpen(false);

    // Always clear the old cookie first to prevent conflicts
    clearGoogleTranslateCookie();
    
    // Remove any googtrans hash from URL
    if (window.location.hash.includes('googtrans')) {
      window.location.hash = '';
    }

    // Set cookie for the selected language (or leave cleared for English)
    if (languageCode !== '') {
      // Set cookie for the selected language
      document.cookie = `googtrans=/en/${languageCode}; path=/; max-age=31536000`; // 1 year
    }
    
    // Always reload the page to apply language change
    window.location.reload();
  };

  if (!shouldTranslate) {
    return null;
  }

  return (
    <div className="google-translate-floating notranslate" ref={dropdownRef}>
      <button
        className="google-translate-btn notranslate"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-label="Translate page"
        title="Translate page"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="currentColor"/>
        </svg>
      </button>

      {isDropdownOpen && (
        <div className="google-translate-dropdown notranslate">
          <div className="dropdown-header notranslate">
            <span className="notranslate">Select Language</span>
          </div>
          <div className="dropdown-list notranslate">
            {languages.map((lang) => (
              <button
                key={lang.code}
                className={`dropdown-item notranslate ${selectedLanguage === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageSelect(lang.code, lang.name)}
              >
                <span className="language-code notranslate">{lang.codeDisplay}</span>
                <span className="language-name notranslate">{lang.name}</span>
                {lang.nativeName && (
                  <span className="language-native notranslate">({lang.nativeName})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Google Translate element - hidden but functional */}
      <div id="google_translate_element" className="google-translate-hidden"></div>
    </div>
  );
};

export default GoogleTranslate;

